// src/pages/Auth.js

import React, {useContext, useEffect, useRef, useState} from 'react';
import {Container, Form, Card, Button, Alert, Col} from 'react-bootstrap';
import {NavLink, useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {
    LOGIN_ROUTE,
    REGISTRATION_ROUTE,
    USER_DASHBOARD_ROUTE,
    RESET_PASSWORD_ROUTE,
    SET_NEW_PASSWORD_ROUTE,
    VERIFY_EMAIL_ROUTE
} from "../../../utils/consts";
import {Context} from "../../../index";
import {
    login,
    registration,
    forgotPassword,
    resetPassword,
    googleLoginSendToken,
    preRegistration
} from "../../../http/userAPI";
import {observer} from "mobx-react-lite";
import {toast} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {GoogleLogin} from '@react-oauth/google';
import Row from "react-bootstrap/Row";
import {FaEnvelope, FaSave} from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";
import * as FingerprintJS from "@fingerprintjs/fingerprintjs";
import SEO from "../../../components/seo/SEO";
import {resolveLang, stripLangFromPath, withLang} from "../../../i18n/routesLang";
import {getAuthContent} from "./auth.content";

const Auth = observer(() => {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const [captchaValue, setCaptchaValue] = useState(null);
    const {user, affiliateStore} = useContext(Context);
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const isMobile = typeof window !== "undefined" && window.innerWidth < 972;

    const recaptchaRef = useRef(null);

    const [isDisabled, setIsDisabled] = useState(false);
    const [timer, setTimer] = useState(null);
    const [deviceId, setDeviceId] = useState(null);
    const [honeypot, setHoneypot] = useState(""); // Honeypot-поле

    // Определяем режим: login, registration, resetPassword, setNewPassword
    const basePath = stripLangFromPath(location.pathname); // "/es/login" -> "/login"
    const seoLang = resolveLang({pathname: location.pathname, storeLang: null}); // для SEO/locale
    const lang = resolveLang({pathname: location.pathname, storeLang: user?.lang});

    const isLogin = basePath === LOGIN_ROUTE;
    const isRegistration = basePath === REGISTRATION_ROUTE;
    const isResetPassword = basePath === RESET_PASSWORD_ROUTE;
    const isSetNewPassword = basePath === SET_NEW_PASSWORD_ROUTE;

    const t = getAuthContent(seoLang);

    // Поля формы
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [error, setError] = useState("");
    const [eerror, setEError] = useState("");
    const [typingTimeout, setTypingTimeout] = useState(null);

    // Получаем токен из URL при сбросе пароля
    const resetToken = searchParams.get("token");

    const validateInputs = () => {
        const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{5,}$/;
        const emailRegex = /^(?!.*[а-яА-ЯёЁ])[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!passwordRegex.test(password) && password.length > 0) {
            setError(t.validation.passRule);
        } else {
            setError("");
        }

        if (!emailRegex.test(email) && email.length > 0) {
            setEError(t.validation.emailRule);
        } else {
            setEError("");
        }
    };

    useEffect(() => {
        const ref = searchParams.get("ref");
        if (ref) {
            localStorage.setItem("ref", ref);
            console.log("Referral ID overwritten:", ref);
        }
    }, [searchParams]);

    useEffect(() => {
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        const timeout = setTimeout(() => validateInputs(), 3000);
        setTypingTimeout(timeout);

        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, password]);

    const handleCaptchaChange = (value) => {
        setCaptchaValue(value);
    };

    useEffect(() => {
        const fetchDeviceId = async () => {
            const fp = await FingerprintJS.load();
            const result = await fp.get();
            const id = result.visitorId;

            setDeviceId(id);
            localStorage.setItem("deviceId", id);
            document.cookie = `deviceId=${id}; path=/; max-age=31536000`; // 1 год
        };

        fetchDeviceId();
    }, []);

    useEffect(() => {
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [timer]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsDisabled(true);
        setErrorMessage('');

        const timeout = setTimeout(() => {
            setIsDisabled(false);
        }, 10000);
        setTimer(timeout);

        if (honeypot) {
            console.warn("Bot detected! Registration blocked.");
            return;
        }

        // **Сброс пароля (если есть токен в URL)**
        if (resetToken) {
            if (!password.trim() || !confirmPassword.trim()) {
                toast.error(t.toasts.enterNewAndConfirm, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return;
            }
            if (password !== confirmPassword) {
                toast.error(t.toasts.passwordsMismatch, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return;
            }

            try {
                await resetPassword(resetToken, password);
                toast.success(t.toasts.resetOk, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                navigate(withLang(LOGIN_ROUTE, lang));
            } catch (error) {
                const msg = error.response?.data?.message || t.toasts.resetErr;
                toast.error(msg, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            }
            return;
        }

        // **Запрос ссылки на сброс пароля**
        if (isResetPassword) {
            if (!email.trim()) {
                toast.error(t.toasts.enterEmail, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return;
            }

            try {
                await forgotPassword(email);
                toast.success(t.toasts.resetLinkSent, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            } catch (error) {
                const msg = error.response?.data?.message || t.toasts.resetLinkErr;
                toast.error(msg, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            }
            return;
        }

        // **Регистрация: проверка совпадения паролей**
        if (isRegistration && password !== confirmPassword) {
            toast.error(t.toasts.passwordsMismatch, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        if ((isLogin || isRegistration) && (!email.trim() || !password.trim())) {
            toast.error(t.toasts.enterEmailAndPass, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        // **Авторизация или регистрация**
        try {
            let data;
            if (isLogin) {
                const token = await recaptchaRef.current.executeAsync();
                recaptchaRef.current.reset();
                data = await login(email, password, token);
                user.setUser(data);
                user.setIsAuth(true);
                navigate(withLang(USER_DASHBOARD_ROUTE, lang));
                user?.setActiveComponent('MainPageDashboard');
            } else {
                const token = await recaptchaRef.current.executeAsync();
                recaptchaRef.current.reset();
                const referrer_id =
                    localStorage.getItem("ref") || affiliateStore?.referrerData?.userId || null;
                data = await preRegistration(email, password, name, token, referrer_id);

                if (data?.status === 'VERIFY_EMAIL_SENT') {
                    const COOLDOWN_SECONDS = 59;
                    const emailLC = email.toLowerCase();
                    localStorage.setItem('pendingVerifyEmail', emailLC);
                    localStorage.setItem(
                        'verifyCooldownUntil',
                        String(Date.now() + COOLDOWN_SECONDS * 1000)
                    );
                    navigate(withLang(VERIFY_EMAIL_ROUTE, lang));
                } else {
                    user.setUser(data);
                    user.setIsAuth(true);
                    navigate(withLang(USER_DASHBOARD_ROUTE, lang));
                    user?.setActiveComponent('MainPageDashboard');
                }
            }
        } catch (e) {
            const status = e?.response?.status;
            const data = e?.response?.data || {};
            const code = data?.code || e?.response?.headers?.['x-error-code'];
            const msg = data?.message || t.toasts.genericError || "Error during request";

            // Точечный кейс: email не подтверждён → добавляем кнопку-переход
            if (status === 403 && (code === 'EMAIL_NOT_VERIFIED' || /verify/i.test(msg))) {
                setErrorMessage(
                    <span>
            {msg}{" "}
                        <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={(ev) => {
                                ev.preventDefault();
                                navigate(withLang(VERIFY_EMAIL_ROUTE, lang));
                            }}
                        >
              {t.toasts.openVerify}
            </button>
          </span>
                );
                return;
            }

            setErrorMessage(msg);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            const {credential} = credentialResponse; // JWT от Google
            const referrer_id = localStorage.getItem("ref") || affiliateStore?.referrerData?.userId || null;
            const gdata = await googleLoginSendToken(credential, referrer_id);

            user.setUser(gdata);
            user.setIsAuth(true);

            setTimeout(() => {
                navigate(withLang(USER_DASHBOARD_ROUTE, lang));
                user?.setActiveComponent('MainPageDashboard');
            }, 1000);
        } catch (error) {
            console.error("Google login failed:", error);
            toast.error(t.toasts.googleFail, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    };

    const BASE_SITE_URL = "https://virtalibry.com";

    const canonicalPath = (() => {
        if (isSetNewPassword) {
            return withLang(SET_NEW_PASSWORD_ROUTE, seoLang);
        }
        if (isResetPassword) return withLang(RESET_PASSWORD_ROUTE, seoLang);
        if (isLogin) return withLang(LOGIN_ROUTE, seoLang);
        if (isRegistration) return withLang(REGISTRATION_ROUTE, seoLang);
        return withLang(basePath, seoLang);
    })();

    const canonicalUrl = `${BASE_SITE_URL}${canonicalPath}`;

    const isAuthLike = isLogin || isRegistration || isResetPassword || isSetNewPassword;

    // SEO-конфиг по типу страницы
    const seoData = (() => {
        const baseImage = t.seo.image || `${BASE_SITE_URL}/preview/virtalibry-auth.jpg`;

        if (isLogin) {
            return {
                title: t.seo.loginTitle,
                description: t.seo.loginDescription,
                keywords: t.seo.loginKeywords,
                url: canonicalUrl,
                image: baseImage,
                faq: t.seo.faq,
                webPage: t.seo.webPage
            };
        }

        if (isRegistration) {
            return {
                title: t.seo.signupTitle,
                description: t.seo.signupDescription,
                keywords: t.seo.signupKeywords,
                url: canonicalUrl,
                image: baseImage,
                faq: t.seo.faq,
                webPage: t.seo.webPage
            };
        }

        if (isResetPassword) {
            return {
                title: t.seo.resetTitle,
                description: t.seo.resetDescription,
                keywords: t.seo.resetKeywords,
                url: canonicalUrl,
                image: baseImage,
                faq: t.seo.faq,
                webPage: t.seo.webPage
            };
        }

        if (isSetNewPassword) {
            return {
                title: t.seo.setNewTitle,
                description: t.seo.setNewDescription,
                keywords: t.seo.setNewKeywords,
                url: canonicalUrl,
                image: baseImage,
                faq: t.seo.faq,
                webPage: t.seo.webPage
            };
        }

        // Фолбэк на общий сайт
        return {
            title: t.seo.title,
            description: t.seo.description,
            keywords: t.seo.keywords,
            url: canonicalUrl,
            image: t.seo.image || baseImage,
            faq: t.seo.faq,
            webPage: t.seo.webPage
        };
    })();

    const breadcrumb = [
        {
            name: t.seo.breadcrumbHomeName,
            item: BASE_SITE_URL
        },
        {
            name: isLogin
                ? t.seo.breadcrumbLoginName
                : isRegistration
                    ? t.seo.breadcrumbRegisterName
                    : isResetPassword
                        ? t.seo.breadcrumbResetName
                        : t.seo.breadcrumbSetNewName,
            item: canonicalUrl
        }
    ];

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage, { pauseOnHover: true, position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    }, [errorMessage]);

    return (
        <>
            <SEO
                title={seoData.title}
                description={seoData.description}
                keywords={seoData.keywords}
                url={seoData.url}
                canonical={canonicalUrl}
                type="website"
                image={seoData.image}
                faq={seoData.faq}
                webPage={seoData.webPage}
                breadcrumb={breadcrumb}
                noindex={isAuthLike}
                nofollow={isAuthLike}
                lang={seoLang}
            />
            <div
                style={{
                    background: "linear-gradient(to right, #f8f9fa, #d4edda)",
                    border: "1px solid #dee2e6",
                    borderRadius: ".2rem",
                    marginTop: "-25px",
                    marginBottom: "-25px",
                    minHeight: "100vh",
                }}
            >
                {!isMobile && (
                <Row className="justify-content-center mt-3 mb-2">
                    <Col xs={12} md={10} lg={10}>
                        <Card className="">
                            <Card.Body className="p-3 p-md-3">
                                {isRegistration && (
                                    <Row className="text-center mt-2 mb-2">
                                        <Col>
                                            {!isMobile && (
                                                <h1 className="text-dark fs-3 text-muted mb-2">
                                                    {t.headings.regTitle}
                                                </h1>
                                            )}
                                            <h2 className="text-dark fs-5 text-muted mb-1">
                                                {t.headings.regSubtitleMain}{" "}
                                                <strong>{t.headings.regSubtitleStrong}</strong>
                                            </h2>
                                            <p className="text-muted mb-0">
                                                {t.headings.regSupportText}
                                            </p>
                                        </Col>
                                    </Row>
                                )}

                                {isLogin && (
                                    <>
                                        {!isMobile && (
                                            <Row className="text-center mt-2 mb-2">
                                                <Col>
                                                    <h1 className="text-dark fs-3 text-muted mb-2">
                                                        {t.headings.loginTitle}
                                                    </h1>
                                                    <h2 className="text-dark fs-5 text-muted mb-1">
                                                        {t.headings.loginHeadline}
                                                    </h2>
                                                    <p className="text-muted mb-0">
                                                        {t.headings.loginSubtitleLine1}
                                                    </p>

                                                </Col>
                                            </Row>
                                        )}
                                    </>
                                )}
                                {isResetPassword && (
                                    <>
                                        {!isMobile && (
                                            <Row className="text-center mt-2 mb-2">
                                                <Col>
                                                    <h1 className="text-dark fs-3 text-muted mb-2">
                                                        {t.headings.recoveryTitle}
                                                    </h1>
                                                    <h2 className="text-dark fs-5 text-muted mb-1">
                                                        {t.headings.recoveryHeadline}
                                                    </h2>
                                                    <p className="text-muted mb-0">
                                                        {t.headings.recoverySubtitleLine1}
                                                    </p>
                                                </Col>
                                            </Row>
                                        )}
                                    </>
                                )}


                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                    )}


                    <Row className="justify-content-center mt-0 mb-2">
                        <Col xs={12} md={10} lg={10}>
                            <Card className="">
                                <Card.Body className="p-3 p-md-4">
                                    <Row className="align-items-center g-4">


                                        {!isMobile && (
                                            <Col md={6} className="d-flex justify-content-end">
                                                <Card
                                                    className=""

                                                >
                                                    <Card.Body className="p-2">
                                                        <Card.Img
                                                            src="../face/face_img_reg_sm.jpg"
                                                            alt="Virtalibry"
                                                            className="img-fluid rounded"
                                                            style={{
                                                                width: "auto",
                                                                height: "auto",
                                                                objectFit: "scale-down",
                                                            }}
                                                        />
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}

                                        <Col xs={12} md={isMobile ? 12 : 6} className="d-flex justify-content-center">
                                            <Card
                                                className="shadow-sm w-100 border-secondary"
                                                style={{ maxWidth: "500px" }}
                                            >
                                                <Card.Header className="py-1 d-flex align-items-center justify-content-between">
                                <span className="small fw-semibold text-success">
                                    {isLogin
                                        ? t.card.headerLogin
                                        : isRegistration
                                            ? t.card.headerSignup
                                            : isSetNewPassword
                                                ? t.card.headerSetNewPassword
                                                : t.card.headerRecovery}
                                </span>
                                                </Card.Header>

                                                <Card.Body className="py-2 px-3">
                                                    <Form className="w-100" onSubmit={handleSubmit}>
                                                        {isRegistration && (
                                                            <Form.Group className="mb-2">
                                                                <Form.Label className="small text-muted mb-1">
                                                                    {t.form.nameLabel}
                                                                </Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="text"
                                                                    placeholder={t.form.namePh}
                                                                    value={name}
                                                                    onChange={(e) => setName(e.target.value)}
                                                                    required
                                                                />
                                                            </Form.Group>
                                                        )}

                                                        {!resetToken && (
                                                            <Form.Group className="mb-2">
                                                                <Form.Label className="small text-muted mb-1">
                                                                    {t.form.emailLabel}
                                                                </Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="email"
                                                                    placeholder={t.form.emailPh}
                                                                    value={email}
                                                                    onChange={(e) => setEmail(e.target.value)}
                                                                    style={{ borderColor: eerror ? "#dc3545" : undefined }}
                                                                    required
                                                                />
                                                                {eerror && (
                                                                    <div className="small text-danger mt-1">{eerror}</div>
                                                                )}
                                                            </Form.Group>
                                                        )}

                                                        {(isLogin || isRegistration || resetToken) && (
                                                            <Form.Group className="mb-2">
                                                                <Form.Label className="small text-muted mb-1">
                                                                    {t.form.passwordLabel}
                                                                </Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="password"
                                                                    placeholder={t.form.passwordPh}
                                                                    value={password}
                                                                    onChange={(e) => setPassword(e.target.value)}
                                                                    style={{ borderColor: error ? "#dc3545" : undefined }}
                                                                    required
                                                                />
                                                                {error && (
                                                                    <div className="small text-danger mt-1">{error}</div>
                                                                )}
                                                            </Form.Group>
                                                        )}

                                                        {(isRegistration || resetToken) && (
                                                            <Form.Group className="mb-2">
                                                                <Form.Label className="small text-muted mb-1">
                                                                    {t.form.confirmLabel}
                                                                </Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="password"
                                                                    placeholder={t.form.confirmPh}
                                                                    value={confirmPassword}
                                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                                    required
                                                                />
                                                            </Form.Group>
                                                        )}

                                                        <div className="mt-3">
                                                            {(isLogin || isRegistration) && googleClientId ? (
                                                                <div className="row g-2">
                                                                    <div className="col-6">
                                                                        <Button
                                                                            size="sm"
                                                                            type="submit"
                                                                            variant="success"
                                                                            disabled={isDisabled || !!error || !!eerror}
                                                                            className="fw-medium"
                                                                            style={{ minHeight: "38px", fontSize: "0.95rem" }}
                                                                        >
                                                                            {isLogin
                                                                                ? t.form.btnLogin
                                                                                : isRegistration
                                                                                    ? t.form.btnCreate
                                                                                    : resetToken
                                                                                        ? t.form.btnSavePassword
                                                                                        : t.form.btnSendReset}
                                                                        </Button>
                                                                    </div>

                                                                    <div className="col-6 d-flex align-items-center justify-content-end">
                                                                        <GoogleLogin
                                                                            onSuccess={handleGoogleSuccess}
                                                                            onError={() =>
                                                                                toast.error(t.toasts.googleAuthFailed, { position: "bottom-center", autoClose: 3000, hideProgressBar: false })
                                                                            }
                                                                            theme={isMobile ? "filled_blue" : "outline"}
                                                                            size="large"
                                                                            type={isMobile ? "icon" : "standard"}
                                                                            shape="rectangular"
                                                                            logo_alignment="left"
                                                                            locale={seoLang}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    type="submit"
                                                                    variant="success"
                                                                    disabled={isDisabled || !!error || !!eerror}
                                                                    className="w-100"
                                                                    style={{ minHeight: "38px" }}
                                                                >
                                                                    {isLogin
                                                                        ? t.form.btnLogin
                                                                        : isRegistration
                                                                            ? t.form.btnCreate
                                                                            : resetToken
                                                                                ? t.form.btnSavePassword
                                                                                : t.form.btnSendReset}
                                                                </Button>
                                                            )}

                                                            {isLogin && (
                                                                <div className="mt-2">
                                                                    <NavLink
                                                                        to={withLang(RESET_PASSWORD_ROUTE, seoLang)}
                                                                        className="small text-success text-decoration-none"
                                                                    >
                                                                        {t.form.forgot}
                                                                    </NavLink>
                                                                </div>
                                                            )}

                                                            {affiliateStore?.referrerData?.ref_name && isRegistration && (
                                                                <div className="small text-success mt-2">
                                                                    {t.form.invitedBy} {affiliateStore?.referrerData?.ref_name}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <input
                                                            type="text"
                                                            name="honeypot"
                                                            value={honeypot}
                                                            onChange={(e) => setHoneypot(e.target.value)}
                                                            style={{ display: "none" }}
                                                            autoComplete="off"
                                                            tabIndex="-1"
                                                        />
                                                    </Form>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>


                <Row className="justify-content-center mb-0">
                    <Col xs={12} md={10} lg={10}>
                        <div className="d-flex flex-column flex-md-row justify-content-center gap-2 text-center">
                            <div className="small text-muted">{t.headings.point1}</div>
                            <div className="small text-muted">{t.headings.point2}</div>
                            <div className="small text-muted">{t.headings.point3}</div>
                        </div>
                    </Col>
                </Row>

                    {!isResetPassword && (
                        <ReCAPTCHA
                            sitekey={process.env.REACT_APP_RE_CAPTCHA_SITE_KEY}
                            size="invisible"
                            ref={recaptchaRef}
                            onChange={handleCaptchaChange}
                        />
                    )}


            </div>
        </>
    );
});

export default Auth;
